import { AfterViewInit, Directive, ElementRef, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { fromEvent, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { assert, Resolution, ResolutionService } from 'ats-ui-lib';
import { TranslateService } from '@ngx-translate/core';

@Directive({
  selector: '[atsPageBackArrow]',
  standalone: false,
})
export class PageBackArrowDirective implements AfterViewInit, OnDestroy {
  private destroySubject = new Subject<void>();

  private arrowElement?: HTMLElement;

  private isMobile$ = this.resolutionService.resolution$.pipe(map((r) => r <= Resolution.Medium));

  private get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  constructor(
    private elementRef: ElementRef,
    private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private resolutionService: ResolutionService,
    private translateService: TranslateService,
  ) {}

  private setArrowVisibility(): void {
    this.isMobile$.pipe(takeUntil(this.destroySubject)).subscribe((isMobile) => {
      if (isMobile) {
        this.showArrow(true);
        this.showBreadCrumb(false);
      } else {
        this.showArrow(false);
        this.showBreadCrumb(true);
      }
    });
  }

  private showBreadCrumb(value: boolean): void {
    const breadcrumb: HTMLElement | null = this.element.querySelector('po-breadcrumb');
    if (breadcrumb) {
      breadcrumb.style.display = value ? 'block' : 'none';
    }
  }

  private showArrow(value: boolean): void {
    assert(this.arrowElement);
    this.arrowElement.style.display = value ? 'inline-block' : 'none';
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }

  ngAfterViewInit(): void {
    const title: HTMLElement | null = this.element.querySelector('.po-page-header-title');
    if (title == null) {
      return;
    }
    this.arrowElement = this.createArrowElement();
    title.prepend(this.arrowElement);
    title.style.display = 'flex';
    title.style.alignItems = 'center';
    title.style.columnGap = '8px';
    this.setArrowVisibility();
  }

  private createArrowElement(): HTMLElement {
    const span = this.document.createElement('span');
    span.classList.add('an', 'an-caret-left');
    span.style.fontSize = '24px';
    span.style.color = 'var(--color-primary)';
    span.style.cursor = 'pointer';
    span.style.display = 'none';
    span.setAttribute('role', 'button');
    span.setAttribute('tabindex', '0');
    this.translateService
      .get('l-back')
      .pipe(takeUntil(this.destroySubject))
      .subscribe((label) => {
        span.setAttribute('aria-label', label);
      });
    const goBack = (): void => {
      this.window.history.back();
    };
    fromEvent(span, 'click').pipe(takeUntil(this.destroySubject)).subscribe(goBack);
    fromEvent(span, 'keypress')
      .pipe(
        takeUntil(this.destroySubject),
        filter((e) => {
          const event = e as KeyboardEvent;
          return event.key === 'Enter' || event.key === ' ';
        }),
      )
      .subscribe(goBack);
    return span;
  }
}
